/**
 * PDF Compressor API client. Mirrors the shape of the server routes
 * under /api/pdf.
 */

export type PdfPreset = 'screen' | 'ebook' | 'printer'

export type PdfStatus =
  | 'pending'
  | 'compressing'
  | 'completed'
  | 'failed'
  | 'expired'
  | 'canceled'

export interface PdfJob {
  jobId: string
  originalFileName: string
  originalSizeBytes: number
  compressedSizeBytes: number
  compressionRatio: number
  preset: PdfPreset
  status: PdfStatus
  errorMessage: string
  completedAt: string | null
  expiresAt: string | null
  createdAt: string
}

export interface PdfStatusResponse {
  activeCount: number
  activeLimit: number
  uploadLimitBytes: number
}

export interface PdfHistoryResponse {
  jobs: PdfJob[]
}

const DEFAULT_TIMEOUT_MS = 30 * 1000 // PDFs can take a few seconds to upload

async function fetchWithTimeout(
  input: RequestInfo,
  init?: RequestInit,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<Response> {
  const ctrl = new AbortController()
  const t = setTimeout(() => ctrl.abort(), timeoutMs)
  try {
    return await fetch(input, { ...init, signal: ctrl.signal })
  } finally {
    clearTimeout(t)
  }
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: { error?: string } | undefined
    try {
      body = (await res.json()) as { error?: string }
    } catch {
      // ignore
    }
    throw new Error(body?.error ?? `http_${res.status}`)
  }
  return (await res.json()) as T
}

export async function getPdfStatus(): Promise<PdfStatusResponse> {
  const res = await fetchWithTimeout('/api/pdf/status', {
    credentials: 'same-origin',
  })
  return jsonOrThrow<PdfStatusResponse>(res)
}

export async function uploadPdf(
  file: File,
  preset: PdfPreset,
): Promise<{ jobId: string }> {
  // We send the raw PDF bytes with application/pdf — the server's
  // express.raw({type:'application/pdf'}) handler parses req.body as
  // Buffer. No multipart, no extra dep.
  const url = `/api/pdf/upload?preset=${preset}&filename=${encodeURIComponent(file.name)}`
  // Generous timeout. 500MB at a typical office uplink (~50 Mbps) is
  // ~80 sec; on a slow home connection it can stretch past 10 min.
  // 20 min covers most realistic cases without giving up early.
  const res = await fetchWithTimeout(
    url,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/pdf' },
      body: file,
    },
    20 * 60 * 1000,
  )
  return jsonOrThrow<{ jobId: string }>(res)
}

export async function getPdfJob(jobId: string): Promise<PdfJob> {
  const res = await fetchWithTimeout(`/api/pdf/job/${jobId}/status`, {
    credentials: 'same-origin',
  })
  return jsonOrThrow<PdfJob>(res)
}

export async function cancelPdfJob(jobId: string): Promise<void> {
  const res = await fetchWithTimeout(`/api/pdf/cancel/${jobId}`, {
    method: 'POST',
    credentials: 'same-origin',
  })
  if (!res.ok && res.status !== 204) {
    throw new Error(`http_${res.status}`)
  }
}

export async function getPdfHistory(): Promise<PdfJob[]> {
  const res = await fetchWithTimeout('/api/pdf/history', {
    credentials: 'same-origin',
  })
  const data = await jsonOrThrow<PdfHistoryResponse>(res)
  return data.jobs
}

export function pdfDownloadUrl(jobId: string): string {
  return `/api/pdf/job/${jobId}/file`
}
